import React, { useState } from 'react';
import SectionTitle from '../../yourProperties/Title';
import useTranslate from '../../../../customHooks/useTranslate';
import Card from '../../../../muiComponents/Card';
import Grid from '../../../../muiComponents/Grid';
import GridChild from '../../../../muiComponents/GridChild';
import Title from '../../../components/Title';
import InfoIcon from '../../../../components/InfoIcon';
import {
    doubleColumnGrid,
    singleColumnGrid,
} from '../../../../utils/bookings/bookingUtils';
import { formatDate, formatDateTime } from '../../../../utils/formatDates';
import CopyOnHover from '../../components/CopyOnHover';
import useLanguage from '../../../../customHooks/useLanguage';
import useTimeZone from '../../../../customHooks/useTimeZone';
import IconButton from '../../../../muiComponents/IconButton';
import { formatNumber } from '../../../../utils/usefulFunctions';
import {
    gridSpacing20,
    gridSpacing30,
} from '../../../../utils/commonVariables/grid';

const MainInformation = props => {
    const {
        bookingCode,
        hotelRefecerenceCode,
        bookingDate,
        checkIn,
        checkOut,
        customerName,
        nights,
        paxes,
        totalAmount,
        currency,
        isCancelled,
        hasSupplements,
        supplements,
        supplementsDetailsLink,
    } = props;
    const title = useTranslate('main_information_booking');
    const translate = useTranslate();
    const language = useLanguage();
    const timeZone = useTimeZone();

    const [isVisibleSection, setIsVisibleSection] = useState(true);

    const allSupplementsUseSameCurrency =
        hasSupplements &&
        supplements.every(
            supplement => supplement.currencyKey === supplements[0].currencyKey
        );

    const totalAmountSupplements = hasSupplements
        ? supplements.reduce(
              (total, supplement) => total + supplement.amount,
              0
          )
        : 0;

    const supplementsCurrency =
        hasSupplements && allSupplementsUseSameCurrency
            ? supplements[0].currencyKey
            : '';

    return (
        <section className='booking__section'>
            <div
                className={`flex flex-between flex-align-baseline ${
                    !isVisibleSection ? 'border-color-bottom' : ''
                }`}
            >
                <SectionTitle text={title} />
                <IconButton
                    iconName={isVisibleSection ? 'ExpandLess' : 'ExpandMore'}
                    onClick={() => setIsVisibleSection(!isVisibleSection)}
                />
            </div>
            {isVisibleSection ? (
                <Card className='padding20'>
                    <Grid
                        style={{ width: 'auto', margin: 0 }}
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing20}
                    >
                        <GridChild gridProps={singleColumnGrid}>
                            <div className='flex-column'>
                                <Title className='text-capitalize text-secondary-600'>
                                    {translate('booking_code')}
                                </Title>
                                <CopyOnHover textToCopy={bookingCode}>
                                    <p className='font-size-18'>
                                        {bookingCode || '----'}
                                    </p>
                                </CopyOnHover>
                            </div>
                        </GridChild>
                        <GridChild gridProps={singleColumnGrid}>
                            <div className='flex-column'>
                                <Title className='text-capitalize text-secondary-600'>
                                    {translate('booking_reference_hotel')}
                                </Title>
                                <CopyOnHover textToCopy={hotelRefecerenceCode}>
                                    <p className='font-size-18'>
                                        {hotelRefecerenceCode || '----'}
                                    </p>
                                </CopyOnHover>
                            </div>
                        </GridChild>
                        <GridChild gridProps={singleColumnGrid}>
                            <div className='flex-column'>
                                <Title className='text-capitalize text-secondary-600'>
                                    {translate('booking_date')}
                                </Title>
                                <p className='font-size-18'>
                                    {bookingDate
                                        ? formatDateTime(
                                              bookingDate,
                                              language,
                                              timeZone
                                          )
                                        : '--/--/----'}
                                </p>
                            </div>
                        </GridChild>
                        <GridChild gridProps={singleColumnGrid}>
                            <div className='flex-column'>
                                <Title className='text-capitalize text-secondary-600'>
                                    {translate('check_in_out')}
                                </Title>
                                <p className='font-size-18'>
                                    {checkIn
                                        ? formatDate(checkIn)
                                        : '--/--/----'}{' '}
                                    -{' '}
                                    {checkOut
                                        ? formatDate(checkOut)
                                        : '--/--/----'}
                                </p>
                            </div>
                        </GridChild>
                        <GridChild gridProps={singleColumnGrid}>
                            <div className='flex-column'>
                                <Title className='text-capitalize text-secondary-600'>
                                    {translate('nights')}
                                </Title>
                                <p className='font-size-18'>
                                    {nights || '----'}
                                </p>
                            </div>
                        </GridChild>
                        <GridChild gridProps={singleColumnGrid}>
                            <div className='flex-column'>
                                <Title className='text-capitalize text-secondary-600'>
                                    {translate('guests')}
                                </Title>
                                <p className='font-size-18'>
                                    {paxes || '----'}
                                </p>
                            </div>
                        </GridChild>
                        <GridChild
                            gridProps={
                                !hasSupplements
                                    ? singleColumnGrid
                                    : doubleColumnGrid
                            }
                        >
                            <div className='flex-column'>
                                <Title className='text-capitalize text-secondary-600'>
                                    {translate('customer')}
                                </Title>
                                <p className='font-size-18'>
                                    {customerName || '----'}
                                </p>
                            </div>
                        </GridChild>
                        {!hasSupplements && (
                            <GridChild gridProps={singleColumnGrid}>
                                <div className='flex-column'>
                                    <Title className='text-capitalize text-secondary-600'>
                                        {translate('room_price')}
                                    </Title>
                                    <p
                                        className={`font-size-24 ${
                                            isCancelled
                                                ? 'price__cancelled'
                                                : 'text-primary-400'
                                        }`}
                                    >
                                        {currency && totalAmount
                                            ? formatNumber(
                                                  totalAmount,
                                                  'currency',
                                                  currency
                                              )
                                            : formatNumber(
                                                  totalAmount,
                                                  'decimal'
                                              )}
                                    </p>
                                    <p className='font-size-12'>
                                        {currency || '---'}
                                    </p>
                                </div>
                            </GridChild>
                        )}
                    </Grid>
                    {hasSupplements && (
                        <div className='border-color-top'>
                            <div className='flex flex-start'>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td className='padding-inline-20'>
                                                <Title className='text-capitalize text-secondary-600'>
                                                    {translate('room_price')}
                                                </Title>
                                            </td>
                                            <td className='padding-inline-20 text-align-end'>
                                                <p
                                                    className={`font-size-24 ${
                                                        isCancelled
                                                            ? 'price__cancelled'
                                                            : 'text-primary-400'
                                                    }`}
                                                >
                                                    {currency && totalAmount
                                                        ? formatNumber(
                                                              totalAmount,
                                                              'currency',
                                                              currency
                                                          )
                                                        : formatNumber(
                                                              totalAmount,
                                                              'decimal'
                                                          )}
                                                </p>
                                            </td>
                                        </tr>
                                        {allSupplementsUseSameCurrency ? (
                                            <tr>
                                                <td className='padding-inline-20'>
                                                    <div className='flex gap-5'>
                                                        <Title className='text-capitalize text-secondary-600'>
                                                            {translate(
                                                                'supplements'
                                                            )}
                                                        </Title>{' '}
                                                        <InfoIcon tooltipText='additional_supplements_included_info' />
                                                    </div>
                                                </td>
                                                <td className='padding-inline-20 text-align-end'>
                                                    <p
                                                        className={`font-size-18 `}
                                                    >
                                                        {totalAmountSupplements
                                                            ? formatNumber(
                                                                  totalAmountSupplements,
                                                                  'currency',
                                                                  supplementsCurrency
                                                              )
                                                            : formatNumber(
                                                                  totalAmountSupplements,
                                                                  'decimal'
                                                              )}
                                                    </p>
                                                </td>
                                            </tr>
                                        ) : (
                                            <>
                                                <tr>
                                                    <td
                                                        className='padding-inline-20'
                                                        colSpan={2}
                                                    >
                                                        {' '}
                                                        <div className='flex gap-5'>
                                                            <Title className='text-capitalize text-secondary-600'>
                                                                {translate(
                                                                    'supplements'
                                                                )}
                                                            </Title>{' '}
                                                            <InfoIcon tooltipText='additional_supplements_included_info' />
                                                        </div>
                                                    </td>
                                                </tr>
                                                {supplements.map(supplement => (
                                                    <tr key={supplement.id}>
                                                        <td className='padding-inline-20'>
                                                            <p>
                                                                {supplement.name ||
                                                                    '---'}
                                                            </p>
                                                        </td>
                                                        <td className='padding-inline-20 text-align-end'>
                                                            <p
                                                                className={`font-size-18 ${
                                                                    isCancelled
                                                                        ? 'price__cancelled'
                                                                        : ''
                                                                }`}
                                                            >
                                                                {supplement.currencyKey
                                                                    ? formatNumber(
                                                                          supplement.amount,
                                                                          'currency',
                                                                          supplement.currencyKey
                                                                      )
                                                                    : formatNumber(
                                                                          supplement.amount,
                                                                          'decimal'
                                                                      )}
                                                            </p>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {supplementsDetailsLink && (
                                <div className='flex flex-start padding-inline-20 padding-block10'>
                                    <a
                                        href={supplementsDetailsLink}
                                        className='text-secondary-700 font-size-12'
                                    >
                                        {translate('go_to_supplements_details')}
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </Card>
            ) : (
                ''
            )}
        </section>
    );
};

export default MainInformation;
